import { useState, useEffect, useContext, useCallback } from "react";
import { toast } from "react-hot-toast";
import { inviteUser } from "../api/workOrder";
import { AuthContext } from "../context/AuthContext";
import Select from "react-select";

const InviteUserModal = ({ isOpen, onClose, onInviteSent }) => {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    email: "",
    role: "viewer",
    allowedModules: [{ value: "erpag", label: "ERPag" }], // default
  });
  const [errors, setErrors] = useState({});
  const [isInviting, setIsInviting] = useState(false);
  const [touched, setTouched] = useState({});

  // Module options
  const moduleOptions = [
    { value: "erpag", label: "ERPag" },
    { value: "acelynk", label: "AceLynk" },
    { value: "costing", label: "Costing Tool" },
    { value: "user_permissions", label: "User Permission Management" },
    { value: "company_dashboard", label: "Company Dashboard" },
    { value: "sales", label: "Sales" },
  ];

  // All modules selected (for admin)
  const allModules = moduleOptions;

  // When role changes → auto-set modules for admin
  useEffect(() => {
    if (formData.role === "admin") {
      setFormData((prev) => ({
        ...prev,
        allowedModules: allModules, // auto-select all
      }));
    } else if (formData.allowedModules.length === allModules.length) {
      // If switching away from admin, reset to default (only ERPag)
      setFormData((prev) => ({
        ...prev,
        allowedModules: [{ value: "erpag", label: "ERPag" }],
      }));
    }
  }, [formData.role]);

  const validateEmail = useCallback((email) => {
    if (!email.trim()) return "Email is required";
    if (!/^\S+@\S+\.\S+$/.test(email)) return "Invalid email format";
    return "";
  }, []);

  const validateRole = (role) => {
    return ["viewer", "editor", "admin"].includes(role) ? "" : "Invalid role";
  };

  const validateModules = (modules) => {
    if (modules.length === 0) {
      return "No module found ";
    }
    return "";
  };

  const validateForm = () => {
    const newErrors = {};
    newErrors.email = validateEmail(formData.email);
    newErrors.role = validateRole(formData.role);
    newErrors.allowedModules = validateModules(formData.allowedModules);
    setErrors(newErrors);
    return Object.values(newErrors).every((err) => err === "");
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    if (name === "email") {
      setErrors((prev) => ({ ...prev, email: validateEmail(formData.email) }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      if (name === "email") setErrors((prev) => ({ ...prev, email: "" }));
      if (name === "role") setErrors((prev) => ({ ...prev, role: "" }));
    }
  };

  const handleModulesChange = (selectedOptions) => {
    // Only allow change if not admin (admin is locked to all)
    if (formData.role !== "admin") {
      setFormData((prev) => ({
        ...prev,
        allowedModules: selectedOptions || [],
      }));
      if (touched.allowedModules) {
        setErrors((prev) => ({ ...prev, allowedModules: "" }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, role: true, allowedModules: true });
    if (!validateForm()) {
      toast.error("Please fix the errors below.", {
        duration: 4000,
        position: "top-right",
      });
      return;
    }

    setIsInviting(true);
    try {
      const payload = {
        email: formData.email.trim(),
        role: formData.role,
        allowedModules: formData.allowedModules.map((opt) => opt.value),
      };

      const response = await inviteUser(payload);

      toast.success("Invitation sent successfully!", {
        duration: 4000,
        position: "top-right",
      });

      // Reset
      setFormData({
        email: "",
        role: "viewer",
        allowedModules: [{ value: "erpag", label: "ERPag" }],
      });
      setErrors({});
      setTouched({});
      onInviteSent();
      onClose();
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to send invitation";
      toast.error(errorMsg, { duration: 5000, position: "top-right" });
    } finally {
      setIsInviting(false);
    }
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    if (isOpen) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md">
        <h4 className="text-2xl font-bold text-gray-900 mb-6">
          Invite New User
        </h4>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="user@example.com"
              className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                errors.email && touched.email
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />
            {errors.email && touched.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </select>
            {errors.role && touched.role && (
              <p className="text-red-500 text-xs mt-1">{errors.role}</p>
            )}
          </div>

          {/* Allowed Modules */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What can this user access? <span className="text-red-500">*</span>
            </label>
            <Select
              isMulti
              name="allowedModules"
              options={moduleOptions}
              value={formData.allowedModules}
              onChange={handleModulesChange}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              isClearable={false}
              placeholder="Select one or more modules..."
              className="basic-multi-select"
              classNamePrefix="select"

              /* ── 1. PORTAL MENU TO BODY (Fixes Clipping) ── */
              menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              
              /* ── 2. AUTO-FLIP UPWARD IF NEAR BOTTOM ── */
              menuPlacement="auto"
              maxMenuHeight={150}

              styles={{
                /* ── 3. ENSURE PORTAL SITS ABOVE MODAL ── */
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 99999, // Must be higher than modal z-index
                }),

                control: (base, state) => ({
                  ...base,
                  minHeight: "44px",
                  borderRadius: "0.5rem",
                  borderColor:
                    errors.allowedModules && touched.allowedModules
                      ? "#ef4444"
                      : state.isFocused
                        ? "#111827"
                        : "#d1d5db",
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: "#9ca3af",
                  },
                }),

                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused
                    ? "#f3f4f6"
                    : "transparent",
                  color: "#111827",
                  cursor: "pointer",
                  ":active": {
                    backgroundColor: "#e5e7eb",
                  },
                }),

                multiValue: (base) => ({
                  ...base,
                  backgroundColor: "#f3f4f6",
                  borderRadius: "9999px",
                  padding: "2px 4px",
                }),

                multiValueLabel: (base) => ({
                  ...base,
                  color: "#111827",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }),

                multiValueRemove: (base) => ({
                  ...base,
                  color: "#6b7280",
                  borderRadius: "50%",
                  ":hover": {
                    backgroundColor: "#e5e7eb",
                    color: "#111827",
                  },
                }),

                menuList: (base) => ({
                  ...base,
                  maxHeight: "200px",
                  overflowY: "auto",
                }),

                menu: (base) => ({
                  ...base,
                  zIndex: 99999,
                  borderRadius: "0.5rem",
                  overflow: "hidden",
                }),
              }}
            />
            {formData.role === "admin" && (
              <p className="text-xs text-gray-500 mt-1">
                Admins automatically get access to all modules
              </p>
            )}

            {formData.allowedModules.length === 0 && (
              <p className="text-xs mt-2 text-orange-600 font-medium">
               Warning: A user must be assigned at least one module to create an account.
              </p>
            )}

            {errors.allowedModules && touched.allowedModules && (
              <p className="text-red-500 text-xs mt-1">
                {errors.allowedModules}
              </p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isInviting}
              className={`px-5 py-2 rounded-lg text-white font-medium flex items-center gap-2 ${
                isInviting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-black hover:bg-gray-800"
              }`}
            >
              {isInviting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Sending...
                </>
              ) : (
                "Send Invite"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteUserModal;
